import { Trans, useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';
import { useMediaQuery, useTheme } from '@mui/material';

import { useLogin } from '~/components/organisms/auth/hooks/use-login';
import { BUTTON_TYPE } from '~/components/ui/button';
import { DividerText } from '~/components/ui/divider/divider-text';
import { Icon } from '~/components/ui/icon';
import { Link } from '~/components/ui/link';
import { Text } from '~/components/ui/text';
import { CLOUDFLARE_KEY, REDIRECT_URI, TABLET_MIN_WIDTH } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { COLORS } from '~/styles/colors';

import {
  AuthButton,
  LoginSocialGoogleStyled,
  StyledActionsWrapper,
  StyledDividerTextWrapper,
  StyledFormBodyWrapper,
  StyledInput,
  StyledInputWrapper,
} from '../styles/formStyled';

import { StyledTermsAndConditionsWrapper } from './styled';

export default function SignUpForm({
  className,
  cameFromPopup,
  selectedQR,
  isAnonCreateQr,
  openConfirmationPopup,
  qrCode,
}) {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));

  const {
    setTurnstileToken,
    setGoogleToken,
    isEmailValid,
    login,
    handleEmailValue,
    email,
    turnstileToken,
    setIsError,
  } = useLogin({
    cameFromPopup,
    selectedQR,
    isAnonCreateQr,
    openConfirmationPopup,
    qrCode,
  });

  return (
    <>
      {!cameFromPopup && (
        <>
          <Text variant={isMobile ? 'h2' : 'h1.5'} sx={{ textAlign: 'center' }}>
            {t('signUp:title')}
          </Text>
          <Text variant="subheader" sx={{ textAlign: 'center' }}>
            {t('signUp:subtitle')}
          </Text>
        </>
      )}
      <StyledFormBodyWrapper className={className}>
        {!isMobile && (
          <>
            <LoginSocialGoogleStyled
              isOnlyGetToken
              client_id={process.env.GOOGLE_CLIENT_ID}
              redirect_uri={REDIRECT_URI}
              onResolve={({ data }) => {
                setGoogleToken(data);
              }}
              onReject={() => setIsError(t('alerts:socialError'))}
              scope={'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'}
            >
              <AuthButton type={BUTTON_TYPE.outlined} borderColor={COLORS.grey200} color={COLORS.textMain}>
                <Icon withParentColor={false} size={28} className="google-icon" icon="GoogleIcon" />
                {t('signUp:buttons.signUpWithGoogle')}
              </AuthButton>
            </LoginSocialGoogleStyled>

            <StyledDividerTextWrapper>
              <DividerText>{t('signUp:dividerText')}</DividerText>
            </StyledDividerTextWrapper>
          </>
        )}

        <StyledInputWrapper>
          <StyledInput
            icon={<Icon className="icon-mail" icon="Mail" />}
            fullWidth
            placeholder="Enter your email..."
            value={email}
            onChange={handleEmailValue}
            type="email"
            helperText={isEmailValid ? t('errors:emailIsValid') : undefined}
            error={isEmailValid}
          />
        </StyledInputWrapper>

        <Turnstile
          siteKey={CLOUDFLARE_KEY}
          options={{
            theme: 'light',
          }}
          action="login"
          onSuccess={setTurnstileToken}
        />

        <StyledActionsWrapper>
          <AuthButton fullWidth onClick={login} disabled={isEmailValid || !turnstileToken || !email}>
            {t('signUp:buttons.signUp')}
          </AuthButton>
        </StyledActionsWrapper>

        <Text variant="t16Regular" sx={{ textAlign: 'center' }}>
          <Trans
            i18nKey={'signUp:loginLink'}
            components={{
              1: <Link to={PATH.LOGIN} />,
            }}
          />
        </Text>

        {isMobile && (
          <>
            <StyledDividerTextWrapper>
              <DividerText>{t('signUp:google')}</DividerText>
            </StyledDividerTextWrapper>
            <LoginSocialGoogleStyled
              isOnlyGetToken
              client_id={process.env.GOOGLE_CLIENT_ID}
              redirect_uri={REDIRECT_URI}
              onResolve={({ data }) => setGoogleToken(data)}
              onReject={() => setIsError(t('alerts:socialError'))}
              scope={'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'}
            >
              <AuthButton type={BUTTON_TYPE.outlined} borderColor={COLORS.grey200} color={COLORS.textMain}>
                <Icon withParentColor={false} size={28} className="google-icon" icon="GoogleIcon" />
                {t('signUp:buttons.signUpWithGoogle')}
              </AuthButton>
            </LoginSocialGoogleStyled>
          </>
        )}
        {!cameFromPopup && (
          <StyledTermsAndConditionsWrapper>
            <Text sx={{ color: COLORS.grey400, textAlign: 'center' }} variant={isMobile ? 'inputVerySmall' : 'input15'}>
              <Trans
                i18nKey={'signUp:byCreatingAccountYouAgree'}
                components={{
                  1: (
                    <Link
                      to={PATH.TOS}
                      color={COLORS.textMain}
                      underline
                      fontSize={isMobile ? 13 : 15}
                      fontWeight={400}
                    />
                  ),
                  2: (
                    <Link
                      to="/privacy-policy"
                      color={COLORS.textMain}
                      underline
                      fontSize={isMobile ? 13 : 15}
                      fontWeight={400}
                    />
                  ),
                }}
              />
            </Text>
          </StyledTermsAndConditionsWrapper>
        )}
      </StyledFormBodyWrapper>
    </>
  );
}
